import { forwardRef, useEffect, useRef, useState } from 'react';
import useFormErrorFocus from '../../hooks/useFormErrorFocus';

export function reportFormError(form, message, field = null) {
  if (!form) return;
  const target = typeof field === 'string'
    ? [...form.querySelectorAll('input,select,textarea,button,[tabindex]')].find(node => node.name === field || node.id === field || node.id.endsWith(`-${field}`))
    : field;
  form.dispatchEvent(new CustomEvent('booking:form-error', { detail: { message, target } }));
}

export function clearFormError(form, field) {
  if (!form) return;
  form.dispatchEvent(new CustomEvent('booking:form-error-clear', { detail: { field } }));
}

export function fieldValidationMessage(field) {
  const label = (field.labels?.[0] || field.parentElement?.querySelector('label'))?.textContent?.replace(/\s*\*\s*$/, '').trim();
  const name = label || field.getAttribute('aria-label') || 'dieses Feld';
  if (field.validity?.valueMissing) return field.type === 'checkbox'
    ? 'Bitte bestätige diese Angabe.' : `Bitte ${name === 'dieses Feld' ? 'dieses Feld ausfüllen' : `„${name}“ ausfüllen`}.`;
  if (field.type === 'email') return 'Bitte eine gültige E-Mail-Adresse eingeben.';
  if (field.validity?.rangeUnderflow || field.validity?.rangeOverflow) return `Bitte einen Wert im erlaubten Bereich für „${name}“ eingeben.`;
  if (field.validity?.tooShort) return `Bitte mindestens ${field.minLength} Zeichen für „${name}“ eingeben.`;
  return `Bitte die Angabe in „${name}“ prüfen.`;
}

// Default for native constraint validation. Forms with explicit field navigation
// keep their own handler; this component does not compete with its focus actions.
export const Form = forwardRef(function Form({ children, onInvalidCapture, ...props }, forwardedRef) {
  const root = useRef(null), queued = useRef(false);
  const [error, setError] = useState(''), [attempt, setAttempt] = useState(0);
  const [target, setTarget] = useState(null);
  useFormErrorFocus(root, error, false, target, attempt);
  useEffect(() => {
    const form = root.current;
    const report = event => {
      const field = event.detail?.target;
      form?.querySelectorAll('[data-form-first-error]').forEach(node => node.removeAttribute('data-form-first-error'));
      if (field && form.contains(field)) {
        field.setAttribute('data-form-first-error', 'true');
        field.setAttribute('data-validation-error', 'true');
        if (!field.hasAttribute('aria-invalid')) { field.setAttribute('aria-invalid', 'true'); field.setAttribute('data-validation-owns-aria', 'true'); }
      }
      setTarget(field && form.contains(field) ? '[data-form-first-error]' : '[data-shared-form-error]');
      setError(event.detail?.message || 'Bitte die Angaben prüfen.'); setAttempt(value => value + 1);
    };
    const clear = event => {
      const field = event.detail?.field;
      const node = [...form.querySelectorAll('[data-form-first-error]')].find(item => item === field || item.id === field || item.id.endsWith(`-${field}`));
      if (!node) return;
      node.removeAttribute('data-validation-error');
      node.removeAttribute('data-form-first-error');
      if (node.dataset.validationOwnsAria === 'true') {
        node.removeAttribute('aria-invalid'); node.removeAttribute('data-validation-owns-aria');
      }
      setError('');
    };
    form?.addEventListener('booking:form-error-clear', clear);
    form?.addEventListener('booking:form-error', report);
    return () => { form?.removeEventListener('booking:form-error', report); form?.removeEventListener('booking:form-error-clear', clear); };
  }, []);
  const invalid = event => {
    if (onInvalidCapture) { onInvalidCapture(event); return; }
    event.preventDefault();
    const field = event.target;
    field.setAttribute('data-validation-error', 'true');
    if (!field.hasAttribute('aria-invalid')) { field.setAttribute('aria-invalid', 'true'); field.setAttribute('data-validation-owns-aria', 'true'); }
    if (queued.current) return;
    queued.current = true;
    // Browsers can flush microtasks between individual invalid events. Keep
    // the first field selected until the entire native validation pass ends.
    window.setTimeout(() => { queued.current = false; }, 0);
    root.current?.querySelectorAll('[data-form-first-error]').forEach(node => node.removeAttribute('data-form-first-error'));
    field.setAttribute('data-form-first-error', 'true');
    setTarget('[data-form-first-error]'); setError(fieldValidationMessage(field)); setAttempt(value => value + 1);
  };
  return <form {...props} ref={node => { root.current = node; if (typeof forwardedRef === 'function') forwardedRef(node); else if (forwardedRef) forwardedRef.current = node; }} onInvalidCapture={invalid} onInputCapture={event => { props.onInputCapture?.(event); if (event.target.hasAttribute('data-form-first-error') && event.target.validity?.valid) setError(''); }}>
    {error && <p data-shared-form-error role="alert" tabIndex={-1} className="rounded-xl bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    {children}
  </form>;
});
